import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import ScanCropStage from "@/components/scanner/ScanCropStage";

// Iter205 — Rileva se la fotocamera del device è realmente disponibile.
// Regola pragmatica: mobile touch device (Android/iOS) o browser con
// `MediaDevices.getUserMedia`. Il bottone Scanner resta disabilitato su
// desktop con tooltip.
const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  if (typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "")) return true;
  return (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
};

// ==================== FORM FILLER DIALOG (condiviso) ====================
// Utilizzato in Kanban (avanza step con form) e in PracticeDetail
// (pulsante "Compila form" sulla scheda pratica).
//
// Iter140m — evoluzione:
//   • Bivi genitore/figlia via `parent_question_id` + `parent_answer_id`.
//     - Se il parent è a scelta, la figlia appare quando la risposta = parent_answer_id
//       (per multiple_choice, se `parent_answer_id` è tra i selezionati).
//     - Se il parent è di testo/numero/data, la figlia appare quando compilato.
//   • Toggle foto per singola domanda: se `enable_photo=true` la risposta è
//     salvata come oggetto `{ value, photos: [...] }`. Retrocompat: se il valore
//     è scalare/array (formato vecchio), viene letto come `value` puro.
//   • Raggruppamento per `category`: rendering ad Accordion (una tendina per
//     categoria). Le domande senza categoria finiscono in un gruppo "Generale".

// ----- Utility valore/foto (retrocompat con formato piatto) -----
function unwrapAnswer(v) {
  if (v && typeof v === "object" && !Array.isArray(v) && ("value" in v || "photos" in v)) {
    return { value: v.value, photos: Array.isArray(v.photos) ? v.photos : [] };
  }
  return { value: v, photos: [] };
}
function wrapAnswer(value, photos, enablePhoto) {
  if (enablePhoto) return { value, photos: photos || [] };
  return value; // formato legacy piatto
}
function isEmpty(v) {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

// ----- Visibility: risolve bivi genitore/figlia ricorsivamente -----
function isQuestionVisible(q, allQuestions, answers) {
  if (!q.parent_question_id) return true;
  const parent = allQuestions.find(x => x.id === q.parent_question_id);
  if (!parent) return true; // parent rimosso → falla comportare come sempre visibile
  if (!isQuestionVisible(parent, allQuestions, answers)) return false;
  const { value: parentValue } = unwrapAnswer(answers[parent.id]);
  const parentIsChoice = ["single_choice", "multiple_choice", "yes_no"].includes(parent.type);
  if (!q.parent_answer_id || !parentIsChoice) {
    // Parent di testo/numero/data OR parent_answer_id non specificato → visibile quando compilato.
    return !isEmpty(parentValue);
  }
  if (Array.isArray(parentValue)) return parentValue.includes(q.parent_answer_id);
  return parentValue === q.parent_answer_id;
}

export function FormFillerDialog({ open, onClose, practiceId, stepId, stepName, schema, initialAnswers, onSubmitted }) {
  const [answers, setAnswers] = useState(initialAnswers || {});
  const [submitting, setSubmitting] = useState(false);
  const initialSnapshotRef = useRef(JSON.stringify(initialAnswers || {}));
  const [confirmClose, setConfirmClose] = useState(false);
  // Iter140m: stato di espansione delle categorie (accordion). Le apriamo tutte
  // di default alla prima apertura per non "nascondere" il lavoro all'operatore.
  const [openCategories, setOpenCategories] = useState(() => new Set());

  // Iter167 — Reset dei campi SOLO al momento dell'apertura del dialog.
  // In precedenza il reset dipendeva anche da `initialAnswers`, quindi ogni
  // refresh del parent (polling notifiche, reload post-avanza, ecc.) creava
  // una nuova reference dell'oggetto e cancellava ciò che l'operatore stava
  // digitando. Ora usiamo un ref per rilevare la transizione closed → open
  // e resettiamo una sola volta all'apertura effettiva del form.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setAnswers(initialAnswers || {});
      initialSnapshotRef.current = JSON.stringify(initialAnswers || {});
      setConfirmClose(false);
    }
    wasOpenRef.current = open;
    // `initialAnswers` è letto solo alla transizione di apertura; se cambia
    // mentre il dialog è già aperto, NON causa reset (guard sopra).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const questions = (schema?.sections?.[0]?.questions) || [];

  // Iter168 — Linearizzazione: le domande figlie vengono renderizzate
  // IMMEDIATAMENTE sotto il proprio genitore ed ereditano la sua categoria
  // effettiva, così un bivio non spezza mai la continuità visiva.
  const linearizedQuestions = [];
  const effectiveCategoryMap = new Map();
  {
    const byId = new Map(questions.map(q => [q.id, q]));
    const childrenByParent = new Map();
    for (const q of questions) {
      if (q.parent_question_id) {
        if (!childrenByParent.has(q.parent_question_id)) childrenByParent.set(q.parent_question_id, []);
        childrenByParent.get(q.parent_question_id).push(q);
      }
    }
    const visited = new Set();
    const visit = (q, inheritedCat) => {
      if (visited.has(q.id)) return;
      visited.add(q.id);
      const ownCat = (q.category || "").trim() || "Generale";
      const effCat = q.parent_question_id && inheritedCat ? inheritedCat : ownCat;
      effectiveCategoryMap.set(q.id, effCat);
      linearizedQuestions.push(q);
      for (const child of (childrenByParent.get(q.id) || [])) {
        visit(child, effCat);
      }
    };
    // Root = domande senza parent (o con parent inesistente = orfane).
    for (const q of questions) {
      if (!q.parent_question_id || !byId.has(q.parent_question_id)) visit(q, null);
    }
    // Salvagente: figli con parent NON ancora visitato (loop di dati corrotti).
    for (const q of questions) if (!visited.has(q.id)) visit(q, null);
  }

  // Categorie in ordine di prima apparizione secondo l'ordine linearizzato.
  const categories = [];
  const catSet = new Set();
  for (const q of linearizedQuestions) {
    const c = effectiveCategoryMap.get(q.id) || "Generale";
    if (!catSet.has(c)) { catSet.add(c); categories.push(c); }
  }
  // Alla prima apertura del dialog espande TUTTE le categorie.
  useEffect(() => {
    if (open && openCategories.size === 0 && categories.length > 0) {
      setOpenCategories(new Set(categories));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!schema) return null;

  const setAnswer = (qid, newValue, newPhotos, enablePhoto) => {
    setAnswers(a => ({ ...a, [qid]: wrapAnswer(newValue, newPhotos, enablePhoto) }));
  };

  const isDirty = () => JSON.stringify(answers) !== initialSnapshotRef.current;

  const attemptClose = () => {
    if (submitting) return;
    if (isDirty()) setConfirmClose(true); else onClose();
  };
  const discardAndClose = () => { setConfirmClose(false); onClose(); };

  // Iter140m: la validazione dei required tiene conto della visibilità.
  const visibleQuestions = questions.filter(q => isQuestionVisible(q, questions, answers));
  const missingRequired = visibleQuestions.filter(q => {
    if (!q.required) return false;
    const { value } = unwrapAnswer(answers[q.id]);
    return isEmpty(value);
  });

  const doSave = async (isDraft) => {
    if (!isDraft && missingRequired.length > 0) {
      toast.error(`Domande obbligatorie mancanti: ${missingRequired.length}`);
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/practices/${practiceId}/forms/${stepId}`, { answers, is_draft: isDraft });
      initialSnapshotRef.current = JSON.stringify(answers);
      if (isDraft) toast.success("Bozza salvata");
      else { toast.success("Form salvato"); onSubmitted && onSubmitted(); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setSubmitting(false); }
  };

  const toggleCategory = (name) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!v) attemptClose(); }}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onEscapeKeyDown={(e) => { e.preventDefault(); attemptClose(); }}
        onPointerDownOutside={(e) => { e.preventDefault(); attemptClose(); }}
        onInteractOutside={(e) => { e.preventDefault(); attemptClose(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            📋 Form di sopralluogo — {stepName}
          </DialogTitle>
          <DialogDescription>
            Compila tutti i campi obbligatori (*) per poter completare lo step.
            Puoi salvare in bozza e riprendere in seguito.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {questions.length === 0 ? (
            <div className="text-sm text-muted-foreground italic text-center py-6">
              Nessuna domanda configurata. Chiedi all'admin di completare il form nel workflow.
            </div>
          ) : categories.map(catName => {
            const catQuestions = linearizedQuestions.filter(q => (effectiveCategoryMap.get(q.id) || "Generale") === catName);
            const catVisible = catQuestions.filter(q => isQuestionVisible(q, questions, answers));
            if (catVisible.length === 0) return null; // niente visibile → salta categoria
            const opened = openCategories.has(catName);
            const catMissing = catVisible.filter(q => q.required && isEmpty(unwrapAnswer(answers[q.id]).value)).length;
            return (
              <div key={catName} className="border rounded-lg overflow-hidden" data-testid={`form-cat-${catName}`}>
                <button
                  type="button"
                  onClick={() => toggleCategory(catName)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-purple-50 hover:bg-purple-100 border-b"
                  data-testid={`form-cat-toggle-${catName}`}
                >
                  <div className="flex items-center gap-2">
                    {opened ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                    <span className="text-sm font-semibold text-purple-900">{catName}</span>
                    <span className="text-[10px] text-slate-500">({catVisible.length})</span>
                  </div>
                  {catMissing > 0 && (
                    <span className="text-[10px] font-bold text-red-700 bg-red-100 border border-red-200 px-2 py-0.5 rounded">
                      {catMissing} obbligator{catMissing === 1 ? "ia" : "ie"}
                    </span>
                  )}
                </button>
                {opened && (
                  <div className="p-3 space-y-4">
                    {catVisible.map((q) => (
                      <FormQuestion
                        key={q.id}
                        q={q}
                        wrappedValue={unwrapAnswer(answers[q.id])}
                        onChange={(newVal, newPhotos) => setAnswer(q.id, newVal, newPhotos, !!q.enable_photo)}
                        idx={questions.findIndex(x => x.id === q.id) + 1}
                        practiceId={practiceId}
                        stepId={stepId}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={(e) => { e.stopPropagation(); attemptClose(); }} data-testid="form-cancel-btn">Annulla</Button>
          <Button variant="outline" onClick={() => doSave(true)} disabled={submitting} data-testid="save-form-draft">
            Salva bozza
          </Button>
          <Button
            className="ht-gradient border-0 text-white"
            onClick={() => doSave(false)}
            disabled={submitting}
            data-testid="submit-form-and-advance"
          >
            {submitting ? "Salvataggio…" : "Salva e consegna →"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={confirmClose} onOpenChange={(v) => { if (!v) setConfirmClose(false); }}>
      <DialogContent className="max-w-md" data-testid="form-dirty-confirm">
        <DialogHeader>
          <DialogTitle>Modifiche non salvate</DialogTitle>
          <DialogDescription>
            Hai delle risposte non salvate. Se chiudi ora andranno perse.
            Vuoi tornare al form o salvare prima come bozza?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={discardAndClose} className="text-red-700 hover:bg-red-50" data-testid="form-discard-close">
            Chiudi senza salvare
          </Button>
          <Button variant="outline" onClick={async () => { setConfirmClose(false); await doSave(true); onClose(); }} disabled={submitting} data-testid="form-save-draft-and-close">
            Salva bozza e chiudi
          </Button>
          <Button className="ht-gradient border-0 text-white" onClick={() => setConfirmClose(false)} data-testid="form-keep-editing">
            Torna al form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function FormQuestion({ q, wrappedValue, onChange, idx, practiceId, stepId }) {
  const { value, photos } = wrappedValue;
  const setValue = (nv) => onChange(nv, photos);
  const setPhotos = (np) => onChange(value, np);
  const label = (
    <label className="text-sm font-medium block mb-1">
      <span className="text-[10px] font-mono bg-slate-100 rounded px-1.5 py-0.5 mr-2">#{idx}</span>
      {q.label} {q.required && <span className="text-red-500">*</span>}
    </label>
  );
  const testId = `form-q-${q.id}`;

  let input = null;
  if (q.type === "short_text") input = <Input value={value || ""} onChange={(e) => setValue(e.target.value)} data-testid={testId} />;
  else if (q.type === "long_text") input = <Textarea rows={3} value={value || ""} onChange={(e) => setValue(e.target.value)} data-testid={testId} />;
  else if (q.type === "number") input = <Input type="number" value={value ?? ""} onChange={(e) => setValue(e.target.value === "" ? null : Number(e.target.value))} data-testid={testId} />;
  else if (q.type === "date") input = <Input type="date" value={value || ""} onChange={(e) => setValue(e.target.value)} data-testid={testId} />;
  else if (q.type === "yes_no") input = (
    <div className="flex gap-2">
      {["Sì", "No"].map(opt => (
        <button type="button" key={opt} onClick={() => setValue(opt)}
          className={`flex-1 h-10 rounded border text-sm font-medium transition ${value === opt ? "ht-gradient border-0 text-white" : "bg-white hover:border-purple-400"}`}
          data-testid={`${testId}-${opt.toLowerCase()}`}>
          {opt}
        </button>
      ))}
    </div>
  );
  else if (q.type === "single_choice") input = (
    <div className="space-y-1">
      {(q.options || []).map(opt => (
        <label key={opt.id} className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-purple-50" data-testid={`${testId}-opt-${opt.id}`}>
          <input type="radio" name={q.id} value={opt.id} checked={value === opt.id} onChange={() => setValue(opt.id)} />
          <span className="text-sm">{opt.label}</span>
        </label>
      ))}
    </div>
  );
  else if (q.type === "multiple_choice") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (id) => setValue(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
    input = (
      <div className="space-y-1">
        {(q.options || []).map(opt => (
          <label key={opt.id} className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-purple-50" data-testid={`${testId}-opt-${opt.id}`}>
            <input type="checkbox" checked={arr.includes(opt.id)} onChange={() => toggle(opt.id)} />
            <span className="text-sm">{opt.label}</span>
          </label>
        ))}
      </div>
    );
  }
  else if (q.type === "photo_upload") {
    // Retrocompat: la vecchia domanda `photo_upload` continua a funzionare
    // usando `value` come array di foto (schema piatto pre-Iter140m).
    return (
      <div>{label}
        <PhotoUploadField questionId={q.id} questionLabel={q.label || q.text || ""} value={Array.isArray(value) ? value : []} onChange={setValue} testId={testId} maxPhotos={q.max_photos || 15} practiceId={practiceId} stepId={stepId} />
      </div>
    );
  }
  else input = <div className="text-xs text-red-500">Tipo domanda non supportato: {q.type}</div>;

  return (
    <div>
      {label}
      {input}
      {/* Iter140m: se `enable_photo=true` mostriamo un uploader foto accanto */}
      {q.enable_photo && (
        <div className="mt-2">
          <div className="text-[11px] uppercase tracking-widest text-purple-800 font-semibold mb-1">
            📷 Foto (opzionali)
          </div>
          <PhotoUploadField questionId={q.id} questionLabel={q.label || q.text || ""} value={photos} onChange={setPhotos} testId={`${testId}-photos`} maxPhotos={q.max_photos || 10} practiceId={practiceId} stepId={stepId} />
        </div>
      )}
    </div>
  );
}


function PhotoUploadField({ questionId, questionLabel, value, onChange, testId, maxPhotos, practiceId, stepId }) {
  const photos = Array.isArray(value) ? value : [];
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  // Iter205 — Scanner documento (solo mobile): scatta più pagine, unisci in PDF
  const [isMobile] = useState(() => isMobileDevice());
  const scanInputRef = useRef(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanPages, setScanPages] = useState([]); // [{ id, dataUrl }]
  const [scanFilename, setScanFilename] = useState("");
  const [scanSaving, setScanSaving] = useState(false);
  // Iter207 — Stage di ritaglio prospettico + filtro B/N. Ogni foto passa
  // attraverso questo step prima di essere aggiunta a `scanPages`.
  const [cropImage, setCropImage] = useState(null); // dataUrl da ritagliare

  const defaultScanName = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `Scansione_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("question_id", questionId);
    // Iter207.21 — Invia anche la label della domanda per rinominare il file
    // salvato (es. "Foto facciata esterna.jpg") con progressivo se duplicato.
    if (questionLabel) fd.append("question_label", questionLabel);
    const r = await api.post(`/practices/${practiceId}/forms/${stepId}/photos`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  };

  const onFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (photos.length + files.length > maxPhotos) {
      toast.error(`Max ${maxPhotos} foto per domanda`);
      return;
    }
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of files) {
        try {
          const d = await uploadOne(f);
          uploaded.push({ id: d.id, url: d.url, signed_url: d.signed_url, filename: d.filename });
        } catch (err) {
          toast.error(`Errore upload ${f.name}: ${err?.response?.data?.detail || "sconosciuto"}`);
        }
      }
      if (uploaded.length > 0) {
        onChange([...photos, ...uploaded]);
        toast.success(`${uploaded.length} foto caricate`);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  // ===== Iter205 — Scanner flow =====

  const readDataUrl = (file) => new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onerror = () => rej(fr.error);
    fr.onload = () => res(String(fr.result));
    fr.readAsDataURL(file);
  });

  const onScanPageSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (scanInputRef.current) scanInputRef.current.value = "";
    if (files.length === 0) return;
    // Iter207 — Prima di aggiungere la pagina, apriamo il crop stage.
    // Se l'utente seleziona più file in un colpo solo (es. galleria), li
    // accodiamo processandoli uno alla volta: mostriamo il primo, gli altri
    // verranno gestiti nel confirm/cancel del crop.
    try {
      const first = await readDataUrl(files[0]);
      // Extra files vengono aggiunti in una coda semplice sull'oggetto ref
      if (files.length > 1) {
        const rest = [];
        for (let i = 1; i < files.length; i++) {
          try { rest.push(await readDataUrl(files[i])); } catch {}
        }
        pendingCropQueueRef.current = rest;
      } else {
        pendingCropQueueRef.current = [];
      }
      setCropImage(first);
    } catch {
      /* skip */
    }
  };

  // Coda di dataUrl in attesa di crop (utente ha selezionato più foto).
  const pendingCropQueueRef = useRef([]);

  const handleCropConfirm = (dataUrl) => {
    setScanPages((prev) => [...prev, { id: `p_${Date.now()}_${Math.random().toString(36).slice(2)}`, dataUrl }]);
    // Se ci sono altre foto in coda, apri il crop per la prossima
    const q = pendingCropQueueRef.current;
    if (q && q.length > 0) {
      const next = q.shift();
      setCropImage(next);
    } else {
      setCropImage(null);
      setScanOpen(true);
    }
  };

  const handleCropCancel = () => {
    // Skip questa pagina; passa alla prossima in coda se presente
    const q = pendingCropQueueRef.current;
    if (q && q.length > 0) {
      const next = q.shift();
      setCropImage(next);
    } else {
      setCropImage(null);
      // Riapri il modal scan solo se già iniziato (almeno una pagina salvata)
      if (scanPages.length > 0) setScanOpen(true);
    }
  };

  const removeScanPage = (id) => setScanPages((prev) => prev.filter((p) => p.id !== id));

  const openScanner = () => {
    if (!isMobile) return;
    setScanPages([]);
    setScanFilename("");
    // Apri direttamente la fotocamera per la prima pagina
    scanInputRef.current?.click();
  };

  const finalizeScan = () => {
    if (scanPages.length === 0) {
      toast.error("Nessuna pagina scannerizzata");
      return;
    }
    if (!scanFilename.trim()) setScanFilename(defaultScanName());
  };

  const buildPdfBlob = async () => {
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    for (let i = 0; i < scanPages.length; i++) {
      const p = scanPages[i];
      // Carica in Image per sapere dimensioni
      const img = await new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im);
        im.onerror = rej;
        im.src = p.dataUrl;
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      // Fit contain
      const ratio = Math.min(pageW / img.width, pageH / img.height);
      const w = img.width * ratio;
      const h = img.height * ratio;
      const x = (pageW - w) / 2;
      const y = (pageH - h) / 2;
      if (i > 0) pdf.addPage();
      pdf.addImage(p.dataUrl, "JPEG", x, y, w, h);
    }
    return pdf.output("blob");
  };

  const saveScan = async () => {
    const nameFinal = (scanFilename.trim() || defaultScanName()).replace(/\.pdf$/i, "");
    if (photos.length + 1 > maxPhotos) {
      toast.error(`Max ${maxPhotos} allegati per domanda`);
      return;
    }
    setScanSaving(true);
    try {
      const blob = await buildPdfBlob();
      const file = new File([blob], `${nameFinal}.pdf`, { type: "application/pdf" });
      const d = await uploadOne(file);
      onChange([...photos, { id: d.id, url: d.url, signed_url: d.signed_url, filename: d.filename }]);
      toast.success("Scansione salvata");
      setScanPages([]);
      setScanFilename("");
      setScanOpen(false);
    } catch (e) {
      toast.error("Errore salvataggio scansione: " + (e?.response?.data?.detail || e.message || ""));
    } finally {
      setScanSaving(false);
    }
  };

  const removeOne = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="border-2 border-dashed border-purple-300 rounded-md p-3 bg-purple-50/40" data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={onFilesSelected}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
          {photos.map((ph, i) => (
            <div key={ph.id || i} className="relative aspect-square rounded overflow-hidden border bg-white group">
              <img src={ph.signed_url || ph.url} alt={ph.filename || `foto ${i + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeOne(i)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-600 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`${testId}-remove-${i}`}
                aria-label="Rimuovi"
              >×</button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || photos.length >= maxPhotos}
          className="flex-1 h-9 rounded ht-gradient border-0 text-white text-xs font-semibold disabled:opacity-50"
          data-testid={`${testId}-btn`}
        >
          {uploading ? "Caricamento…" : photos.length === 0 ? "📷 Aggiungi foto" : `📷 Aggiungi altre foto (${photos.length}/${maxPhotos})`}
        </button>
        {/* Iter205 — Scansiona documento: solo mobile. Su desktop disabilitato con tooltip. */}
        <button
          type="button"
          onClick={openScanner}
          disabled={!isMobile || uploading || scanSaving || photos.length >= maxPhotos}
          title={!isMobile ? "Disponibile solo da smartphone" : "Scansiona un documento (più pagine)"}
          className="h-9 px-3 rounded border border-purple-300 text-purple-700 bg-white text-xs font-semibold disabled:opacity-50"
          data-testid={`${testId}-scan-btn`}
        >
          📄 Scansiona documento
        </button>
      </div>
      {/* Input dedicato per lo scanner (nascosto). Il `capture=environment`
          apre direttamente la fotocamera posteriore su mobile. */}
      <input
        ref={scanInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={onScanPageSelected}
        className="hidden"
        data-testid={`${testId}-scan-input`}
      />
      <div className="text-[10px] text-slate-500 mt-1.5">
        Max {maxPhotos} allegati, 10 MB ciascuno. Su mobile si aprirà la fotocamera; su desktop la galleria file. Lo scanner genera un PDF unico.
      </div>

      {/* Iter205 — Modal scansione multi-pagina */}
      <Dialog open={scanOpen} onOpenChange={(v) => { if (!scanSaving) setScanOpen(v); }}>
        <DialogContent className="max-w-md" data-testid={`${testId}-scan-modal`}>
          <DialogHeader>
            <DialogTitle>Scansiona documento</DialogTitle>
            <DialogDescription>
              Scatta una foto per ogni pagina. Le pagine verranno unite in un unico PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Miniature scorrevoli */}
            <div className="flex gap-2 overflow-x-auto py-1" data-testid={`${testId}-scan-pages`}>
              {scanPages.length === 0 && (
                <div className="text-xs text-slate-500 italic">Nessuna pagina scannerizzata</div>
              )}
              {scanPages.map((p, idx) => (
                <div key={p.id} className="relative shrink-0 w-20 aspect-[3/4] rounded overflow-hidden border bg-white">
                  <img src={p.dataUrl} alt={`p${idx + 1}`} className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] text-center py-0.5">
                    pag. {idx + 1}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeScanPage(p.id)}
                    className="absolute top-0 right-0 w-5 h-5 bg-red-600 text-white text-xs"
                    aria-label={`Rimuovi pagina ${idx + 1}`}
                  >×</button>
                </div>
              ))}
            </div>
            {/* CTA: aggiungi pagina o concludi */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => scanInputRef.current?.click()}
                disabled={scanSaving}
                data-testid={`${testId}-scan-add-page`}
              >
                📷 Aggiungi pagina
              </Button>
              <Button
                onClick={finalizeScan}
                disabled={scanSaving || scanPages.length === 0}
                className="ht-gradient text-white"
                data-testid={`${testId}-scan-finish`}
              >
                Concludi scansione
              </Button>
            </div>
            {/* Nome file */}
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Nome file</label>
              <Input
                value={scanFilename}
                onChange={(e) => setScanFilename(e.target.value)}
                placeholder={defaultScanName()}
                data-testid={`${testId}-scan-filename`}
              />
              <p className="text-[10px] text-slate-500 mt-1">Verrà salvato come PDF con questo nome.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setScanOpen(false)} disabled={scanSaving}>
              Annulla
            </Button>
            <Button
              onClick={saveScan}
              disabled={scanSaving || scanPages.length === 0}
              className="ht-gradient text-white"
              data-testid={`${testId}-scan-save`}
            >
              {scanSaving ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter207 — Stage crop/deskew/B-N per ogni foto scattata dallo scanner */}
      <ScanCropStage
        open={!!cropImage}
        imageDataUrl={cropImage}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
}
